'use client';

import { useEffect, useState } from 'react';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { Info, Open } from '@drift-labs/icons';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { getApyFromApr } from 'src/utils/borrowLend';
import { getUrlForAccount } from 'src/utils/explorer';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { format } from 'timeago.js';
import Link from 'src/components/primitives/Link/Link';
import { BASE_NAVIGATION_OPTIONS } from 'src/components/Navigation/config/consts';
import { RouteNavigationResult } from 'src/components/Navigation/types';
import { IfVaultPerformanceGraph } from './IFVaultPerformanceGraph';
import {
	DEFAULT_PERFORMANCE_DATA,
	PERFORMANCE_TIMEFRAMES,
} from 'src/@types/types';
import SignDependentColorValue from 'src/components/Utils/SignDependentColorValue';
import {
	PerformanceDataSetKey,
	PnLTimeSeriesDataPoint,
} from 'src/@types/performance';
import { useInsuranceFundRecords } from 'src/hooks/insuranceFund/useInsuranceFundRecords';
import useAccountData from 'src/hooks/useAccountData';
import useUtilizationIsTooHighForIFUnstake from 'src/hooks/useUtilizationIsTooHighForIFUnstake';
import { MarketId } from '@drift/common';
import { useCancelInsuranceFundUnstake } from 'src/hooks/driftActions/insuranceFund/useCancelInsuranceFundUnstake';
import { useWithdrawInsuranceFundAssets } from 'src/hooks/driftActions/insuranceFund/useWithdrawInsuranceFundAssets';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useIfDataQuery } from 'src/hooks/query/ifData/useIfDataQuery';
import {
	POOL_NAMES_BY_POOL_ID,
	ZERO_QUOTE_BIGNUM,
} from 'src/constants/constants';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import { formatMarketSymbolByContext } from 'src/utils/marketSymbol';
import useModal from 'src/components/Modals/hooks/useModal';
import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import Divider from 'src/components/primitives/Divider/Divider';
import Tag from 'src/components/primitives/Tag/Tag';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import Button from 'src/components/primitives/Button/Button';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

const Section = ({
	sectionLabel,
	topRightNode,
	children,
}: {
	sectionLabel: string;
	topRightNode?: React.ReactNode;
	children: React.ReactNode;
}) => {
	return (
		<div className="flex flex-col gap-y-lg">
			<div className="flex w-full items-center justify-between">
				<span className="typo-t3 text-text-default">{sectionLabel}</span>
				{topRightNode && topRightNode}
			</div>
			<div className="flex flex-col gap-y-sm">{children}</div>
		</div>
	);
};

const DetailRow = ({
	label,
	value,
	marketSymbol,
}: {
	label: React.ReactNode;
	value: React.ReactNode;
	marketSymbol?: string;
}) => (
	<div className="flex w-full justify-between">
		<span className="typo-b4 text-text-secondary capitalize">{label}</span>
		<div className="flex items-center gap-1">
			{marketSymbol && (
				<MarketIcon marketSymbol={marketSymbol} sizeClass="w-4 h-4" />
			)}
			<span className="typo-b4 text-text-default">{value}</span>
		</div>
	</div>
);

const DAYS_PER_YEAR = 365;
const SEVEN_DAY_PERIODS = DAYS_PER_YEAR / 7;

function formatPercentChange(pctDiff: number): string {
	const pct = pctDiff * 100;
	if (pct === 0) return '0.00%';
	const decimals = Math.abs(pct) < 0.01 && Math.abs(pct) > 0 ? 4 : 2;
	return `${pct >= 0 ? '+' : ''}${pct.toFixed(decimals)}%`;
}

function computeSevenDayApyFromSharePrices(
	points: PnLTimeSeriesDataPoint[]
): number | undefined {
	if (points.length < 2) return undefined;
	const startPrice = points[0]?.totalPnl ?? 0;
	const endPrice = points[points.length - 1]?.totalPnl ?? 0;
	if (startPrice <= 0) return undefined;
	const sevenDayReturn = (endPrice - startPrice) / startPrice;
	const apy = (Math.pow(1 + sevenDayReturn, SEVEN_DAY_PERIODS) - 1) * 100;
	return isFinite(apy) ? apy : undefined;
}

const useIFVaultPerformanceData = (
	marketIndex: number,
	timeFrame?: PerformanceDataSetKey
) => {
	const { data: allPerformanceData = DEFAULT_PERFORMANCE_DATA } =
		useInsuranceFundRecords(marketIndex);
	const [chartData, setChartData] = useState<PnLTimeSeriesDataPoint[]>([]);
	const [pctDiff, setPctDiff] = useState(0);
	const [computedSevenDayApy, setComputedSevenDayApy] = useState<
		number | undefined
	>(undefined);
	const [selectedTimeFrame, setSelectedTimeFrame] =
		useState<PerformanceDataSetKey>(
			timeFrame ?? (PERFORMANCE_TIMEFRAMES[2] as PerformanceDataSetKey)
		);

	useEffect(() => {
		if (
			allPerformanceData[selectedTimeFrame] &&
			allPerformanceData[selectedTimeFrame].length > 0
		) {
			const data = allPerformanceData[selectedTimeFrame];
			setChartData(data);

			const startValue = data[0]?.totalPnl ?? 0;
			const endValue = data[data.length - 1]?.totalPnl ?? 0;
			const diff = endValue - startValue;
			setPctDiff(startValue !== 0 ? diff / startValue : 0);
		}

		const sevenDayData = allPerformanceData['7d'];
		if (sevenDayData && sevenDayData.length >= 2) {
			setComputedSevenDayApy(computeSevenDayApyFromSharePrices(sevenDayData));
		} else {
			setComputedSevenDayApy(undefined);
		}
	}, [allPerformanceData, selectedTimeFrame]);

	return {
		selectedTimeFrame,
		setSelectedTimeFrame,
		chartData,
		pctDiff,
		computedSevenDayApy,
	};
};

export const InsuranceFundVaultDrawer = () => {
	const marketIndex = useDrawersStore((s) => s.insuranceFundVault.marketIndex);
	const { data: ifData } = useIfDataQuery(marketIndex);
	const { show } = useModal();
	const accountIsDelegate = useAccountData()?.isDelegatedTo;
	const { cancelInsuranceFundUnstakeRequest } = useCancelInsuranceFundUnstake();
	const { withdrawInsuranceFundUnstakedAssets } =
		useWithdrawInsuranceFundAssets();
	const { captureEvent } = usePostHogCapture();
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const [isSubmittingWithdrawTxn, setIsSubmittingWithdrawTxn] = useState(false);
	const [isSubmittingCancelTxn, setIsSubmittingCancelTxn] = useState(false);
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const nextPayoutSeconds = ifData?.vault?.secondsUntilNextRevenueSettle ?? 0;
	const [nextPayoutFormatted, setNextPayoutTs] = useState(
		format(new Date().valueOf() + nextPayoutSeconds * 1000)
	);

	// ensures that when the timer reaches 0 seconds, the next payout time is updated instead of showing the past time
	useEffect(() => {
		const interval = setInterval(() => {
			setNextPayoutTs(format(new Date().valueOf() + nextPayoutSeconds * 1000));
		}, 1000);

		return () => clearInterval(interval);
	}, [nextPayoutSeconds]);

	const unstakeUtilizationCutoffResult = useUtilizationIsTooHighForIFUnstake(
		MarketId.createSpotMarket(marketIndex ?? 0)
	);

	// Vault Performance Graph
	const {
		selectedTimeFrame,
		setSelectedTimeFrame,
		chartData,
		pctDiff,
		computedSevenDayApy,
	} = useIFVaultPerformanceData(marketIndex ?? 0);

	// Early return if data not loaded yet
	if (marketIndex === undefined || !ifData) {
		return null;
	}

	const oraclePrice = getOraclePriceForMarket(
		MarketId.createSpotMarket(ifData.marketIndex)
	);
	const spotMarketConfig = spotMarketsSortedByMarketIndex[ifData.marketIndex];
	const marketSymbol = getCleanedMarketSymbol(spotMarketConfig.symbol);
	const decimals = spotMarketConfig.precisionExp.toNumber();

	// Vault Details - prefer APY computed from share price records when available
	const sevenDayApy = computedSevenDayApy ?? ifData.vault.sevenDayApy ?? 0;
	const nextApr = ifData.vault.nextApr ?? 0;
	const nextApy = getApyFromApr(nextApr, 'hourly');
	const sevenDayApyDisplay =
		!isFinite(sevenDayApy) || sevenDayApy > 10000
			? '> 10,000%'
			: Math.abs(sevenDayApy) < 0.01 && sevenDayApy !== 0
				? `${sevenDayApy.toFixed(4)}%`
				: `${sevenDayApy.toFixed(2)}%`;
	const nextApyDisplay =
		!isFinite(nextApy) || nextApy > 10000
			? '> 10,000%'
			: `${nextApy.toFixed(2)}%`;
	const vaultBalance = ifData.vault.totalStakeBigNum ?? ZERO_QUOTE_BIGNUM;
	const nextVaultPayout = ifData.vault.nextPayoutForStakers;

	// User Details
	const userStake = ifData.userStake.currentStakeBigNum;
	const notionalUserBalance = ifData.userStake?.currentStakeNotionalValue;

	const userSharePct =
		((ifData.userStake.currentStake ?? 0) / (ifData.vault.totalStake ?? 1)) *
		100;
	const userSharePctString =
		userSharePct < 0.01 ? '<0.01' : userSharePct.toFixed(2);

	const userCumulativeEarnings =
		(ifData.userStake.currentStake ?? 0) - (ifData.userStake.costBasis ?? 0);

	const notionalUserCumulativeEarnings = BigNum.fromPrint(
		`${userCumulativeEarnings}`,
		QUOTE_PRECISION_EXP
	).mul(oraclePrice);

	const userNextPayout = (nextVaultPayout ?? 0) * (userSharePct / 100);

	const unstakeRequestTimestamp = ifData?.userStake?.unstakeRequest?.timestamp;
	const unstakePeriod =
		(ifData?.vault?.account?.unstakingPeriod?.toNumber() ?? 0) * 1000;
	const unstakeInProgress = !!unstakeRequestTimestamp;
	const unstakeTimeoutExpired =
		unstakeInProgress && Date.now() >= unstakeRequestTimestamp + unstakePeriod;
	const canWithdraw =
		unstakeInProgress &&
		!unstakeUtilizationCutoffResult.unstakingIsDisabled &&
		unstakeTimeoutExpired;

	const isHourly = selectedTimeFrame === '24h';

	// Vault Revenue Details
	const stakersAprCap =
		(ifData.vault.ratioForStakers ?? 0) * (ifData.vault.maxApr ?? 0);
	const protocolAprCap =
		(1 - (ifData.vault.ratioForStakers ?? 0)) * (ifData.vault.maxApr ?? 0);
	const revenueSettlePeriod = ifData.vault.revenueSettlePeriodMinutes;
	const currentRevenuePool = ifData.vault.revenuePoolBigNum;
	const payoutPerSettlePct = (ifData.vault.payoutRatio ?? 0) * 100;
	const stakersPayoutSharePct = (ifData.vault.ratioForStakers ?? 0) * 100;
	const protocolPayoutSharePct =
		(1 - (ifData.vault.ratioForStakers ?? 0)) * 100;
	const lastPayoutFormatted = format(ifData.vault.lastRevenueSettleMs ?? 0);
	const stakersNextPayout =
		(ifData.vault.nextPayoutTotal ?? 0) * (ifData.vault.ratioForStakers ?? 0);
	const protocolNextPayout =
		(ifData.vault.nextPayoutTotal ?? 0) *
		(1 - (ifData.vault.ratioForStakers ?? 0));

	const handleClickStakeButton = () => {
		// already disabled if delegate, but extra layer of protection in case they inspect element
		if (!accountIsDelegate) {
			show({
				type: 'stake',
				props: {
					selectedIfStakeMarket: spotMarketConfig,
				},
			});
		}
	};

	const handleClickUnstakeButton = () => {
		// already disabled if delegate, but extra layer of protection in case they inspect element
		if (!accountIsDelegate) {
			show({
				type: 'stake',
				props: {
					selectedIfStakeMarket: spotMarketConfig,
					initialTab: 'unstake',
				},
			});
		}
	};

	const handleWithdrawStake = async () => {
		if (!accountIsDelegate && unstakeInProgress && canWithdraw) {
			try {
				setIsSubmittingWithdrawTxn(true);

				await withdrawInsuranceFundUnstakedAssets({
					marketIndex: ifData.marketIndex,
				});

				captureEvent('withdrew_unstaked_assets', {
					spot_market_symbol: spotMarketConfig.symbol,
				});
			} catch (e) {
				console.error(e);
			} finally {
				setIsSubmittingWithdrawTxn(false);
			}
		}
	};

	const handleCancelUnstakeRequest = async () => {
		if (!accountIsDelegate && unstakeInProgress && !canWithdraw) {
			try {
				setIsSubmittingCancelTxn(true);

				await cancelInsuranceFundUnstakeRequest({
					marketIndex: ifData.marketIndex,
				});

				captureEvent('canceled_unstake_request', {
					spot_market_symbol: spotMarketConfig.symbol,
				});
			} catch (e) {
				console.error(e);
			} finally {
				setIsSubmittingCancelTxn(false);
			}
		}
	};

	return (
		<SideDrawer.Container drawerName="insuranceFundVault">
			<SideDrawer.Title>
				<MarketIcon marketSymbol={marketSymbol} />
				<span>
					{formatMarketSymbolByContext(
						spotMarketConfig.symbol,
						'insurance_fund'
					)}
				</span>
				{spotMarketConfig.poolId > 0 && (
					<Tooltip
						decoration="cursor-help"
						content={`This vault insures a market within the ${
							POOL_NAMES_BY_POOL_ID[
								spotMarketConfig.poolId as keyof typeof POOL_NAMES_BY_POOL_ID
							]
						} isolated pool.`}
					>
						<Tag
							className="bg-button-secondary-bg-hover text-text-default"
							rightIcon={Info}
						>
							{
								POOL_NAMES_BY_POOL_ID[
									spotMarketConfig.poolId as keyof typeof POOL_NAMES_BY_POOL_ID
								]
							}
						</Tag>
					</Tooltip>
				)}
			</SideDrawer.Title>
			<SideDrawer.Body>
				{/** About */}
				<Section
					sectionLabel="About"
					topRightNode={
						<Link
							href={getUrlForAccount(
								ifData.vault.account?.vault.toBase58() ?? ''
							)}
							openInNewTab
							variant="secondary"
							className="typo-t4"
						>
							View on Explorer
						</Link>
					}
				>
					<DetailRow
						label="APY (7D)"
						value={
							<span className="text-text-positive-green-button">
								{sevenDayApyDisplay}
							</span>
						}
					/>
					<DetailRow
						label="Projected APY (hourly)"
						value={
							<span className="text-text-positive-green-button">
								{nextApyDisplay}
							</span>
						}
					/>
					<DetailRow
						label="Vault balance"
						value={vaultBalance.prettyPrint()}
						marketSymbol={marketSymbol}
					/>
					<DetailRow
						label={`Next payout ${nextPayoutFormatted}`}
						value={nextVaultPayout?.toFixed(decimals) ?? '0'}
						marketSymbol={marketSymbol}
					/>
				</Section>

				<Divider />

				{/** Your Info */}
				<Section
					sectionLabel="Your Info"
					topRightNode={
						<Link
							href={
								(
									BASE_NAVIGATION_OPTIONS.insuranceFundStakingHistory.handler() as RouteNavigationResult
								).route
							}
							variant="secondary"
							rightIcon={Open}
							openInNewTab
							className="typo-t4"
						>
							Staking History
						</Link>
					}
				>
					<DetailRow
						label="Your balance"
						value={userStake?.prettyPrint() ?? '0'}
						marketSymbol={marketSymbol}
					/>
					<DetailRow
						label="Notional balance"
						value={notionalUserBalance?.toNotional() ?? '$0.00'}
					/>
					<DetailRow label="Your share" value={`${userSharePctString}%`} />
					<DetailRow
						label={`Your next payout ${nextPayoutFormatted}`}
						value={userNextPayout.toFixed(decimals)}
						marketSymbol={marketSymbol}
					/>
					<DetailRow
						label="Your earnings"
						value={userCumulativeEarnings.toFixed(decimals)}
						marketSymbol={marketSymbol}
					/>
					<DetailRow
						label="Notional earnings"
						value={notionalUserCumulativeEarnings?.toNotional() ?? '$0.00'}
					/>
				</Section>

				<Divider />

				{/** Vault Performance */}
				<Section
					sectionLabel="Vault Performance (by Share Price)"
					topRightNode={
						<SignDependentColorValue value={pctDiff}>
							{formatPercentChange(pctDiff)}
						</SignDependentColorValue>
					}
				>
					<IfVaultPerformanceGraph chartData={chartData} isHourly={isHourly} />

					<Tabs
						options={PERFORMANCE_TIMEFRAMES.map((label) => ({
							label: label === 'all' ? '90d' : label,
							value: label,
							className: 'flex-1',
						}))}
						activeTab={selectedTimeFrame}
						onTabChange={(val) =>
							setSelectedTimeFrame(val as PerformanceDataSetKey)
						}
						className="w-full"
						size="md"
						variant="secondary"
					/>
				</Section>

				<Divider />

				{/** Vault Revenue Details */}
				<Section sectionLabel="Vault Revenue Details">
					<DetailRow
						label="APR cap"
						value={`${stakersAprCap.toFixed(
							2
						)}% (stakers) / ${protocolAprCap.toFixed(2)}% (protocol)`}
					/>
					<DetailRow
						label="Revenue settle period"
						value={`${revenueSettlePeriod} minutes`}
					/>

					<Divider />

					<DetailRow
						label="Current revenue pool"
						value={currentRevenuePool?.prettyPrint() ?? '0'}
						marketSymbol={marketSymbol}
					/>
					<DetailRow
						label="Payout per settle"
						value={`${payoutPerSettlePct}%`}
					/>
					<DetailRow
						label="Share of payout"
						value={`${stakersPayoutSharePct}% (stakers) / ${protocolPayoutSharePct}% (protocol)`}
					/>
					<DetailRow
						label="Next payout"
						value={
							<div className="flex flex-col items-end gap-1">
								<div className="flex items-center gap-1">
									<MarketIcon marketSymbol={marketSymbol} sizeClass="h-4 w-4" />
									<span>{stakersNextPayout.toFixed(decimals)} (stakers)</span>
								</div>
								<div className="flex items-center gap-1">
									<MarketIcon marketSymbol={marketSymbol} sizeClass="h-4 w-4" />
									<span>{protocolNextPayout.toFixed(decimals)} (protocol)</span>
								</div>
							</div>
						}
					/>
					<DetailRow label="Next revenue settle" value={nextPayoutFormatted} />
					<DetailRow label="Last revenue settle" value={lastPayoutFormatted} />
				</Section>
			</SideDrawer.Body>
			<SideDrawer.Footer>
				{!unstakeInProgress && (
					<>
						<Button
							variant="primary"
							size="lg"
							onClick={handleClickStakeButton}
							// Disable stake when Privy-native for now.
							disabled={isPrivyNativeAuth}
							className="flex-1"
						>
							Stake
						</Button>
						{userStake?.gtZero() && (
							<Button
								variant="secondary"
								size="lg"
								onClick={handleClickUnstakeButton}
								className="flex-1"
							>
								Unstake
							</Button>
						)}
					</>
				)}

				{unstakeInProgress &&
					(canWithdraw ? (
						<Button
							variant="secondary"
							size="lg"
							onClick={handleWithdrawStake}
							isLoading={isSubmittingWithdrawTxn}
							className="w-full"
						>
							Withdraw Stake
						</Button>
					) : (
						<Button
							variant="secondary"
							size="lg"
							onClick={handleCancelUnstakeRequest}
							isLoading={isSubmittingCancelTxn}
							className="w-full"
						>
							Cancel Unstake
						</Button>
					))}
			</SideDrawer.Footer>
		</SideDrawer.Container>
	);
};
